import React, { useState, useEffect } from "react";
import { orderService } from "../services/dataService";
import { OrderImage } from "../types";
import { getImageUrl } from "../utils/formatters";
import { IMAGE_BASE_URL } from "../services/api";
import AlertModal from "./AlertModal";
import ConfirmModal from "./ConfirmModal";
import "./ImageManager.css";

interface ImageManagerProps {
  orderId: string;
  onClose: () => void;
}

const ImageManager: React.FC<ImageManagerProps> = ({ orderId, onClose }) => {
  const [images, setImages] = useState<OrderImage[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [imagePreviewModal, setImagePreviewModal] = useState<{
    show: boolean;
    imageUrl: string;
  }>({ show: false, imageUrl: "" });

  // Alert Modal state
  const [alertModal, setAlertModal] = useState<{
    isOpen: boolean;
    message: string;
    type: "info" | "success" | "warning" | "error";
  }>({ isOpen: false, message: "", type: "info" });

  // Confirm Modal state
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    message: string;
    type: "info" | "warning" | "danger";
    onConfirm: () => void;
  }>({ isOpen: false, message: "", type: "warning", onConfirm: () => {} });

  useEffect(() => {
    loadImages();
  }, [orderId]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadImages = async () => {
    try {
      setLoading(true);
      const orderImages = await orderService.getImages(orderId);
      setImages(orderImages);
    } catch (error) {
      console.error("Images could not be loaded:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    try {
      setUploading(true);
      const result = await orderService.uploadImages(orderId, files);
      await loadImages(); // Refresh the image list
      setAlertModal({
        isOpen: true,
        message: `${result.images.length} resim başarıyla yüklendi!`,
        type: "success",
      });
    } catch (error) {
      console.error("Image upload failed:", error);
      setAlertModal({
        isOpen: true,
        message: "Resim yükleme sırasında hata oluştu!",
        type: "error",
      });
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteImage = async (imageId: string) => {
    setConfirmModal({
      isOpen: true,
      message: "Bu resmi silmek istediğinizden emin misiniz?",
      type: "danger",
      onConfirm: async () => {
        try {
          await orderService.deleteImage(imageId);
          await loadImages(); // Refresh the image list
          setAlertModal({
            isOpen: true,
            message: "Resim başarıyla silindi!",
            type: "success",
          });
        } catch (error) {
          console.error("Image deletion failed:", error);
          setAlertModal({
            isOpen: true,
            message: "Resim silme sırasında hata oluştu!",
            type: "error",
          });
        }
      },
    });
  };

  const handleDownloadImage = async (imageUrl: string) => {
    try {
      // Image URL'den sadece path'i al
      const urlPath = imageUrl.replace(/^https?:\/\/[^\/]+/, "");

      // Backend download endpoint'ini kullan (CORS sorunu yok)
      const downloadUrl = `${IMAGE_BASE_URL}/api/Order/download-image?path=${encodeURIComponent(urlPath)}`;

      // Link oluştur ve tıkla
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = `image-${Date.now()}.jpg`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Image download failed:", error);
      setAlertModal({
        isOpen: true,
        message: "Resim indirme sırasında hata oluştu!",
        type: "error",
      });
    }
  };

  return (
    <div className="image-manager-overlay">
      <div className="image-manager-modal">
        <div className="image-manager-header">
          <h3>
            <span className="header-icon">
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                <circle cx="8.5" cy="8.5" r="1.5"></circle>
                <polyline points="21 15 16 10 5 21"></polyline>
              </svg>
            </span>
            Sipariş Resimleri
            {!loading && images.length > 0 && (
              <span className="image-count-badge">{images.length} resim</span>
            )}
          </h3>
          <button className="close-button" onClick={onClose}>
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>

        <div className="image-manager-content">
          {/* Upload Section */}
          <div className="upload-section">
            <label htmlFor="image-upload" className="btn btn-primary">
              {uploading ? "Yükleniyor..." : "Resim Ekle"}
            </label>
            <input
              id="image-upload"
              type="file"
              multiple
              accept="image/*"
              onChange={handleFileUpload}
              disabled={uploading}
              style={{ display: "none" }}
            />
          </div>

          {/* Images Grid */}
          {loading ? (
            <div className="loading">
              <div className="loading-spinner"></div>
              <div>Resimler yükleniyor...</div>
            </div>
          ) : (
            <div className="images-grid">
              {images.length === 0 ? (
                <div className="no-images">
                  <div className="no-images-icon">🖼️</div>
                  Bu sipariş için henüz resim yüklenmemiş.
                </div>
              ) : (
                images.map((image) => {
                  const imageUrl = getImageUrl(image.imageUrl);

                  return (
                    <div key={image.orderImageId} className="image-item">
                      <div className="image-wrapper">
                        <img
                          src={imageUrl}
                          alt={image.description || "Sipariş resmi"}
                          className="order-image"
                        />
                        <div className="image-overlay">
                          <button
                            className="overlay-btn imgm-btn-preview"
                            title="Önizleme"
                            onClick={() =>
                              setImagePreviewModal({ show: true, imageUrl })
                            }
                          >
                            <svg
                              width="16"
                              height="16"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                              <circle cx="12" cy="12" r="3"></circle>
                            </svg>
                          </button>
                          <button
                            className="overlay-btn imgm-btn-download"
                            title="İndir"
                            onClick={() => handleDownloadImage(imageUrl)}
                          >
                            <svg
                              width="16"
                              height="16"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                              <polyline points="7 10 12 15 17 10"></polyline>
                              <line x1="12" y1="15" x2="12" y2="3"></line>
                            </svg>
                          </button>
                          <button
                            className="overlay-btn imgm-btn-delete"
                            title="Sil"
                            onClick={() =>
                              handleDeleteImage(image.orderImageId)
                            }
                          >
                            <svg
                              width="16"
                              height="16"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="#ffffff"
                              strokeWidth="2.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              style={{ display: "block", flexShrink: 0 }}
                            >
                              <polyline points="3 6 5 6 21 6"></polyline>
                              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                              <line x1="10" y1="11" x2="10" y2="17"></line>
                              <line x1="14" y1="11" x2="14" y2="17"></line>
                            </svg>
                          </button>
                        </div>
                      </div>
                      <div className="image-info">
                        <p className="image-description">{image.description}</p>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          )}
        </div>
      </div>

      {/* Image Preview Modal */}
      {imagePreviewModal.show && (
        <div
          className="image-preview-modal"
          onClick={() => setImagePreviewModal({ show: false, imageUrl: "" })}
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.9)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 10001,
            cursor: "pointer",
          }}
        >
          <button
            onClick={() => setImagePreviewModal({ show: false, imageUrl: "" })}
            style={{
              position: "absolute",
              top: "20px",
              right: "20px",
              background: "white",
              border: "none",
              borderRadius: "50%",
              width: "40px",
              height: "40px",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 2px 10px rgba(0,0,0,0.3)",
              zIndex: 10002,
              transition: "transform 0.2s, background 0.2s",
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.transform = "scale(1.1)";
              e.currentTarget.style.background = "#f8f9fa";
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.transform = "scale(1)";
              e.currentTarget.style.background = "white";
            }}
            title="Kapat"
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleDownloadImage(imagePreviewModal.imageUrl);
            }}
            style={{
              position: "absolute",
              top: "20px",
              right: "70px",
              background: "#28a745",
              color: "white",
              border: "none",
              borderRadius: "50%",
              width: "40px",
              height: "40px",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 2px 10px rgba(0,0,0,0.3)",
              zIndex: 10002,
              transition: "transform 0.2s, background 0.2s",
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.transform = "scale(1.1)";
              e.currentTarget.style.background = "#218838";
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.transform = "scale(1)";
              e.currentTarget.style.background = "#28a745";
            }}
            title="Resmi İndir"
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
              <polyline points="7 10 12 15 17 10"></polyline>
              <line x1="12" y1="15" x2="12" y2="3"></line>
            </svg>
          </button>
          <img
            src={imagePreviewModal.imageUrl}
            alt="Büyük önizleme"
            onClick={(e) => e.stopPropagation()}
            style={{
              maxWidth: "90%",
              maxHeight: "90%",
              objectFit: "contain",
              borderRadius: "8px",
              boxShadow: "0 4px 20px rgba(0,0,0,0.5)",
            }}
          />
        </div>
      )}

      {/* Alert Modal */}
      <AlertModal
        isOpen={alertModal.isOpen}
        message={alertModal.message}
        type={alertModal.type}
        onClose={() => setAlertModal({ ...alertModal, isOpen: false })}
      />

      {/* Confirm Modal */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        message={confirmModal.message}
        type={confirmModal.type}
        onConfirm={() => {
          confirmModal.onConfirm();
          setConfirmModal({ ...confirmModal, isOpen: false });
        }}
        onCancel={() => setConfirmModal({ ...confirmModal, isOpen: false })}
      />
    </div>
  );
};

export default ImageManager;
